<template>
  <div class="app-overlay">
    <!-- AppModal's -->
    <transition :name="overlayStore.activeAppModalTransition">
      <component
        :is="components[overlayStore.activeAppModal]"
        :options="overlayStore.activeAppModalOptions"
      />
    </transition>

    <!-- AppTray's -->
    <transition :name="overlayStore.activeAppTrayTransition">
      <component
        :is="components[overlayStore.activeAppTray]"
        :options="overlayStore.activeAppTrayOptions"
      />
    </transition>

    <!-- FlyIn's -->
    <transition :name="overlayStore.activeFlyInTransition">
      <component
        :is="components[overlayStore.activeFlyIn]"
        :options="overlayStore.activeFlyInOptions"
      />
    </transition>
  </div>
</template>

<script setup>
  import { computed, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import { APP_MODAL, FLY_IN, APP_TRAY } from '~/lib/constants/overlay'
  import { useMainStore } from '~/stores/main'
  import { useOverlayStore } from '~/stores/overlay'

  // Stores
  const mainStore = useMainStore()
  const overlayStore = useOverlayStore()

  // Modals
  const ConfirmationModal = defineAsyncComponent(() => import('~/components/confirmation-modal.vue'))
  const DeleteImagesModal = defineAsyncComponent(() => import('~/components/delete-images-modal.vue'))
  const UploadImagesModal = defineAsyncComponent(() => import('~/components/upload-images-modal.vue'))
  const UploadImagesPreviewModal = defineAsyncComponent(() => import('~/components/upload-images-preview-modal.vue'))
  const EventDeleteModal = defineAsyncComponent(() => import('~/components/event-delete-modal.vue'))
  const UserDeleteModal = defineAsyncComponent(() => import('~/components/user-delete-modal.vue'))
  const EventResetModal = defineAsyncComponent(() => import('~/components/event-reset-modal.vue'))
  const EventUpgradeModal = defineAsyncComponent(() => import('~/components/event-upgrade-modal.vue'))
  const RemoveConnectorModal = defineAsyncComponent(() => import('~/components/remove-connector-modal'))
  const AdminDiscountCodeModal = defineAsyncComponent(() => import('~/components/admin-discount-code-modal.vue'))
  const ImageCommentEditModal = defineAsyncComponent(() => import('~/components/image-comment-edit-modal.vue'))
  const ResetCustomEventSortingConfirmationModal = defineAsyncComponent(
    () => import('~/components/reset-custom-event-sorting-confirmation-modal.vue'),
  )
  const DeleteImageCommentConfirmationModal = defineAsyncComponent(
    () => import('~/components/delete-image-comment-confirmation-modal.vue'),
  )
  const LoginModal = defineAsyncComponent(() => import('~/components/login-modal.vue'))
  const EventExtendModal = defineAsyncComponent(() => import('~/components/event-exend-modal.vue'))
  const IosUploadWarningModal = defineAsyncComponent(() => import('~/components/ios-upload-warning-modal.vue'))
  const IosDownloadChoiceModal = defineAsyncComponent(() => import('~/components/ios-download-choice-modal.vue'))
  const AlbumDeleteModal = defineAsyncComponent(() => import('~/components/album-delete-modal.vue'))
  const MoveToAlbumModal = defineAsyncComponent(() => import('~/components/move-to-album-modal.vue'))
  const GuestbookComposerModal = defineAsyncComponent(() => import('~/components/guestbook-composer-modal.vue'))

  // AppTray's
  const SortMediaControlsTray = defineAsyncComponent(() => import('~/components/sort-media-controls-tray.vue'))
  const SelectMediaControlsTray = defineAsyncComponent(() => import('~/components/select-media-controls-tray.vue'))
  const SortModeTray = defineAsyncComponent(() => import('~/components/sort-mode-tray.vue'))

  // FlyIn's
  const GeneralNavigationFlyIn = defineAsyncComponent(() => import('~/components/general-navigation-fly-in.vue'))
  const AccountNavigationFlyIn = defineAsyncComponent(() => import('~/components/account-navigation-fly-in.vue'))

  const components = {
    ConfirmationModal,
    DeleteImagesModal,
    UploadImagesModal,
    UploadImagesPreviewModal,
    EventDeleteModal,
    UserDeleteModal,
    EventResetModal,
    EventUpgradeModal,
    RemoveConnectorModal,
    AdminDiscountCodeModal,
    ImageCommentEditModal,
    ResetCustomEventSortingConfirmationModal,
    DeleteImageCommentConfirmationModal,
    LoginModal,
    SortMediaControlsTray,
    SelectMediaControlsTray,
    SortModeTray,
    GeneralNavigationFlyIn,
    AccountNavigationFlyIn,
    EventExtendModal,
    IosUploadWarningModal,
    IosDownloadChoiceModal,
    AlbumDeleteModal,
    MoveToAlbumModal,
    GuestbookComposerModal,
  }

  const { isMobile } = storeToRefs(mainStore)
  const { desktop, mobile, settings } = storeToRefs(overlayStore)

  const desktopSettings = computed(() =>
    desktop.value
      ? { ...settings.value, ...desktop.value, options: { ...settings.value?.options, ...desktop.value?.options } }
      : settings.value,
  )
  const mobileSettings = computed(() =>
    mobile.value
      ? { ...settings.value, ...mobile.value, options: { ...settings.value?.options, ...mobile.value?.options } }
      : settings.value,
  )
  const overlaySettings = computed(() => (isMobile.value ? mobileSettings.value : desktopSettings.value))

  watch(overlayStore, () => setCorrectOverlay())

  function setCorrectOverlay() {
    if (!overlaySettings.value) {
      return
    }

    if (overlaySettings.value.type === APP_MODAL) {
      overlayStore.setActiveAppModalTransition(overlaySettings.value?.transition)
      overlayStore.setActiveAppModalOptions(overlaySettings.value.options)
      overlayStore.setActiveAppModal(overlaySettings.value.component)
    }

    if (overlaySettings.value.type === APP_TRAY) {
      overlayStore.setActiveAppTrayTransition(overlaySettings.value?.transition ?? 'app-tray-transition')
      overlayStore.setActiveAppTrayOptions(overlaySettings.value.options)
      overlayStore.setActiveAppTray(overlaySettings.value.component)
    }

    if (overlaySettings.value.type === FLY_IN) {
      overlayStore.setActiveFlyInTransition(overlaySettings.value?.transition ?? 'fly-in-transition')
      overlayStore.setActiveFlyInOptions(overlaySettings.value.options)
      overlayStore.setActiveFlyIn(overlaySettings.value.component)
    }
  }
</script>

<style lang="scss">
  .app-overlay {
    position: relative;
    // Same layer as the fullscreen image viewer (app-image-modal), which was
    // raised to z 3 to cover the sticky album filter/FAB (both z 2). Dialogs
    // opened FROM the viewer (edit/delete comment, confirmations) live in
    // this wrapper and win by later DOM position — at z 2 the whole overlay
    // stacking context was capped BEHIND the viewer's image.
    z-index: z('app-modal');
  }
</style>
